/**
 * Snapshot interpolation for drawn units. The sim moves units once per tick
 * (10 Hz), and ticks arrive with network jitter, so drawing the newest tick's
 * spot (or chasing it) makes drawn speed pulse. Instead each unit keeps two
 * timed points: where it was drawn when the latest tick arrived, and that
 * tick's spot, reached when the sim's clock (`TickClock`) says it's due,
 * about one tick later. Drawing lerps between them, so units are shown about
 * one tick behind the sim at a steady speed. A jump too long to be a walk (a
 * teleport) snaps instead.
 */

/** A drawn unit's two timed points (world x/z, seconds). */
export interface Motion {
  fromX: number;
  fromZ: number;
  toX: number;
  toZ: number;
  /** When it leaves `from` (the latest tick's arrival). */
  t0: number;
  /** How long it takes to reach `to`. */
  duration: number;
}

/** Sim seconds per tick (`NetworkedSimRunner`'s `DT`). */
export const TICK_SECONDS = 0.1;
/** Drawn this far behind the sim at least: one tick, so there's a next spot to head for. */
const BASE_DELAY = TICK_SECONDS;
/** Most extra delay arrival jitter can add. */
const MAX_JITTER_DELAY = 0.1;
/** How quickly the offset creeps up after its lower envelope (per arrival). */
const OFFSET_RISE = 0.02;
/** How quickly the jitter estimate follows arrivals (per arrival). */
const JITTER_EMA = 0.05;
/**
 * Arrivals this far off the estimate (a stall that dropped sim time, a
 * resume, a new host) restart it: past the most delay there is, a lasting
 * shift would leave every segment due before it starts.
 */
const RESYNC = BASE_DELAY + MAX_JITTER_DELAY;

export function motionAt(x: number, z: number, now: number): Motion {
  return { fromX: x, fromZ: z, toX: x, toZ: z, t0: now, duration: 0 };
}

/** Where `m` is drawn at `now`. */
export function sampleMotion(m: Motion, now: number, out: { x: number; z: number }): void {
  const f = m.duration <= 0 ? 1 : Math.min(1, Math.max(0, (now - m.t0) / m.duration));
  out.x = m.fromX + (m.toX - m.fromX) * f;
  out.z = m.fromZ + (m.toZ - m.fromZ) * f;
}

/**
 * Maps sim ticks to local time from their arrivals: `offset` follows the
 * lower envelope of `arrival − tick × TICK_SECONDS` (the least-delayed
 * arrivals), and `jitter` how much later than that ticks typically land. A
 * tick's spot is drawn at `due(tick)`, so segments run on the sim's even
 * clock rather than on uneven arrivals.
 */
export class TickClock {
  private offset: number | null = null;
  private jitter = 0;

  arrive(tick: number, arrival: number): void {
    const sample = arrival - tick * TICK_SECONDS;
    if (this.offset === null || Math.abs(sample - this.offset) > RESYNC) {
      this.offset = sample;
      this.jitter = 0;
      return;
    }
    this.offset =
      sample < this.offset ? sample : this.offset + (sample - this.offset) * OFFSET_RISE;
    this.jitter += (sample - this.offset - this.jitter) * JITTER_EMA;
  }

  /** When `tick`'s spot should be reached on screen. */
  due(tick: number): number {
    const delay = BASE_DELAY + Math.min(MAX_JITTER_DELAY, 2 * this.jitter);
    return tick * TICK_SECONDS + (this.offset ?? 0) + delay;
  }
}

const scratch = { x: 0, z: 0 };

/**
 * A new tick arrived at `arrival`: head from wherever `m` is drawn then to the
 * tick's spot, reaching it at `due`, or snap there if it's further than
 * `snapDistance` (a teleport, not a walk).
 */
export function retarget(
  m: Motion,
  toX: number,
  toZ: number,
  arrival: number,
  due: number,
  snapDistance: number
): void {
  sampleMotion(m, arrival, scratch);
  const dx = toX - scratch.x;
  const dz = toZ - scratch.z;
  const teleport = dx * dx + dz * dz > snapDistance * snapDistance;
  m.fromX = teleport ? toX : scratch.x;
  m.fromZ = teleport ? toZ : scratch.z;
  m.toX = toX;
  m.toZ = toZ;
  m.t0 = arrival;
  m.duration = teleport ? 0 : Math.max(0, due - arrival);
}
